import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { apiFetch } from './api'

interface Admin {
  id: string
  email: string
  name?: string
  role: string
}

interface AuthContextType {
  user: Admin | null
  loading: boolean
  sendMagicLink: (email: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Admin | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkSession()
  }, [])

  async function checkSession() {
    const token = localStorage.getItem('digsafe_admin_token')
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const data = await apiFetch('/api/admin/profile')
      setUser(data.admin)
    } catch {
      localStorage.removeItem('digsafe_admin_token')
    }
    setLoading(false)
  }

  async function sendMagicLink(email: string) {
    await apiFetch('/api/auth/send-magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  function logout() {
    localStorage.removeItem('digsafe_admin_token')
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, sendMagicLink, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
